import { supabase } from './supabase';

export interface Payment {
  id: string;
  business_id: string;
  booking_id?: string | null;
  proposal_id?: string | null;
  gift_card_id?: string | null;
  amount: number;
  tip_amount?: number;
  base_amount?: number;
  surcharge_amount?: number;
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other';
  channel?: string | null;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  payment_type?: 'deposit' | 'final' | 'partial';
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  pdf_url?: string;
  payment_token?: string;
  online_payment_url?: string;
  stripe_payment_intent_id?: string;
  stripe_checkout_session_id?: string;
  booking_number?: number;
  booking_title?: string;
  booking_total?: number;
  booking_subtotal?: number;
  booking_tax_amount?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  booking_services?: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    tax_amount: number;
    total_amount: number;
    duration_minutes: number;
    vehicle_make: string;
    vehicle_model: string;
  }[];
  booking_discounts?: {
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }[];
}

export async function getPayments(businessId: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments_with_details')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

async function getBookingPayments(bookingId: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments_with_stripe_details')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

/** Normalized channel for the Payments ledger. */
export type PaymentChannel =
  | 'cash'
  | 'debit'
  | 'credit'
  | 'card_online'
  | 'card_tap_to_pay'
  | 'card_reader'
  | 'gift_card'
  | 'wallet'
  | 'bank_transfer'
  | 'check'
  | 'financing'
  | 'other';

/** Best-effort channel from the legacy `method` when a caller doesn't specify one. */
function defaultChannelForMethod(
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other',
): PaymentChannel {
  switch (method) {
    case 'cash': return 'cash';
    case 'debit_card': return 'debit';
    case 'bank_transfer': return 'bank_transfer';
    case 'check': return 'check';
    case 'credit_card': return 'credit';
    default: return 'other';
  }
}

export const createPayment = async ({
  business_id,
  booking_id,
  proposal_id,
  project_id,
  gift_card_id,
  invoice_id: invoiceIdParam,
  product_sale_id,
  amount,
  method,
  channel,
  status = 'completed',
  reference = null,
  notes = null,
  payment_type = 'final'
}: {
  business_id: string;
  booking_id?: string | null;
  proposal_id?: string | null;
  project_id?: string | null;
  gift_card_id?: string | null;
  /** Standalone invoice (e.g. order/counter-sale) this payment settles.
   *  When set, the invoice allocation trigger applies it to that invoice. */
  invoice_id?: string | null;
  product_sale_id?: string | null;
  amount: number;
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other';
  channel?: PaymentChannel;
  status?: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  reference?: string | null;
  notes?: string | null;
  payment_type?: 'deposit' | 'final' | 'partial';
}): Promise<Payment> => {
  console.log('💰 [PAYMENT DEBUG] Starting payment creation...');
  console.log('💰 [PAYMENT DEBUG] Payment params:', {
    business_id,
    booking_id,
    proposal_id,
    project_id,
    gift_card_id,
    amount,
    method,
    status,
    reference,
    notes,
    payment_type
  });

  if (!supabase) {
    console.error('❌ [PAYMENT DEBUG] Supabase client not initialized');
    throw new Error('Supabase client not initialized');
  }

  console.log('💰 [PAYMENT DEBUG] Creating payment record in database...');
  // Round amount to 2 decimal places to prevent fractional cent issues
  const roundedAmount = Math.round(amount * 100) / 100;

  // Stamp the invoice this booking payment belongs to (traceability). The DB
  // rollup also matches by booking_id, so this is best-effort and non-fatal.
  let invoice_id: string | null = invoiceIdParam ?? null;
  if (!invoice_id && booking_id) {
    try {
      const { data: inv } = await supabase
        .from('invoices')
        .select('id')
        .eq('booking_id', booking_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      invoice_id = inv?.id ?? null;
    } catch {
      /* non-fatal: trigger still matches by booking_id */
    }
  }

  const { data: payment, error } = await supabase
    .from('payments')
    .insert({
      business_id,
      booking_id: booking_id || null,
      proposal_id: proposal_id || null,
      project_id: project_id || null,
      gift_card_id: gift_card_id || null,
      invoice_id,
      product_sale_id: product_sale_id || null,
      amount: roundedAmount,
      method,
      channel: channel || defaultChannelForMethod(method),
      status,
      reference,
      notes,
      payment_type: payment_type
    })
    .select()
    .single();

  console.log('💰 [PAYMENT DEBUG] Payment creation result:', { payment, error });

  if (error) {
    console.error('❌ [PAYMENT DEBUG] Payment creation failed:', error);
    throw new Error(`Failed to create payment: ${error.message}`);
  }

  console.log('✅ [PAYMENT DEBUG] Payment created successfully with ID:', payment.id);

  // Check if this is the first real payment and handle unpaid invoice replacement
  // Only do this for booking payments, not gift card payments
  if (booking_id) {
    console.log('🔍 [PAYMENT DEBUG] Checking for unpaid invoice to replace...');
    try {
      // Look for existing unpaid invoice (pending status with 0 amount)
      const { data: unpaidInvoices, error: unpaidError } = await supabase
        .from('payments')
        .select('*')
        .eq('booking_id', booking_id)
        .eq('status', 'pending')
        .eq('amount', 0)
        .eq('reference', 'unpaid-invoice');

    if (!unpaidError && unpaidInvoices && unpaidInvoices.length > 0) {
      console.log('📄 [PAYMENT DEBUG] Found unpaid invoice to replace:', unpaidInvoices[0].id);
      
      // Delete the unpaid invoice payment record and its PDF
      const unpaidInvoice = unpaidInvoices[0];
      
      // Try to delete the PDF file from storage if it exists
      if (unpaidInvoice.pdf_url) {
        try {
          const urlParts = unpaidInvoice.pdf_url.split('/');
          const filename = urlParts[urlParts.length - 1];
          console.log('🗑️ [PAYMENT DEBUG] Deleting unpaid invoice PDF:', filename);
          
          await supabase.storage
            .from('receipts')
            .remove([filename]);
          
          console.log('✅ [PAYMENT DEBUG] Unpaid invoice PDF deleted successfully');
        } catch (deleteError) {
          console.error('❌ [PAYMENT DEBUG] Failed to delete unpaid invoice PDF:', deleteError);
        }
      }
      
      // Delete the unpaid payment record
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('id', unpaidInvoice.id);

      if (deleteError) {
        console.error('❌ [PAYMENT DEBUG] Failed to delete unpaid invoice record:', deleteError);
      } else {
        console.log('✅ [PAYMENT DEBUG] Unpaid invoice record deleted successfully');
      }
    } else {
      console.log('ℹ️ [PAYMENT DEBUG] No unpaid invoice found - this is a subsequent payment');
    }
    } catch (replaceError) {
      console.error('❌ [PAYMENT DEBUG] Error during unpaid invoice replacement:', replaceError);
      // Continue without throwing - payment was created successfully
    }
  } else {
    console.log('ℹ️ [PAYMENT DEBUG] Skipping unpaid invoice check - not a booking payment');
  }

  // Generate PDF invoice via server-side edge function (non-blocking)
  if (booking_id) {
    try {
      console.log('📄 [PAYMENT] Generating PDF via edge function for booking:', booking_id);
      const { data: pdfResult, error: pdfError } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { bookingId: booking_id }
      });

      if (pdfError) {
        console.warn('⚠️ [PAYMENT] PDF generation failed:', pdfError);
      } else if (pdfResult?.url) {
        const { error: updateError } = await supabase
          .from('payments')
          .update({ pdf_url: pdfResult.url })
          .eq('id', payment.id);
        if (!updateError) {
          payment.pdf_url = pdfResult.url;
          console.log('✅ [PAYMENT] Payment PDF URL set:', pdfResult.url);
        }
      }
    } catch (pdfError) {
      console.warn('⚠️ [PAYMENT] PDF generation error (non-blocking):', pdfError);
    }
  }

  // Paid / partially_paid automations now fire server-side from a DB trigger on
  // the payments table (automation-payment-completed edge function), so every
  // payment path — not just this one — triggers them exactly once.
  if (booking_id) {
    // Process referral reward if booking becomes fully paid
    // Only process if payment status is 'completed'
    if (status === 'completed') {
      console.log('🎁 [PAYMENT DEBUG] Checking if referral reward should be processed...');
      try {
        // Dynamically import to avoid circular dependencies
        const { processReferralReward } = await import('./referralProgram');
        const rewardResult = await processReferralReward(booking_id, business_id);
        if (rewardResult.success) {
          console.log('✅ [PAYMENT DEBUG] Referral reward processed successfully');
        } else if (rewardResult.error && !rewardResult.error.includes('already processed') && !rewardResult.error.includes('not fully paid')) {
          // Only log warnings for unexpected errors, not for expected conditions
          console.warn('⚠️ [PAYMENT DEBUG] Referral reward processing:', rewardResult.error);
        }
      } catch (rewardError) {
        console.error('❌ [PAYMENT DEBUG] Error processing referral reward:', rewardError);
        // Continue without throwing - payment was created successfully
      }
    }
  } else {
    console.log('ℹ️ [PAYMENT DEBUG] Skipping automation trigger - not a booking payment');
  }

  console.log('🎉 [PAYMENT DEBUG] Payment creation process completed successfully');
  return payment;
};

export async function updatePayment(id: string, updates: Partial<Payment>) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

async function deletePayment(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function voidPayment(id: string, reason: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'voided',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

/**
 * Completely void/delete a payment - removes payment record and PDF entirely
 */
export async function completelyVoidPayment(paymentId: string, reason: string): Promise<void> {
  console.log('🗑️ [VOID DEBUG] Starting complete void process for payment:', paymentId);
  
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    // Step 1: Get the payment record to access PDF URL
    console.log('🔍 [VOID DEBUG] Fetching payment record...');
    const { data: payment, error: fetchError } = await supabase
      .from('payments')
      .select('*')
      .eq('id', paymentId)
      .single();

    if (fetchError) {
      console.error('❌ [VOID DEBUG] Failed to fetch payment:', fetchError);
      throw new Error(`Failed to fetch payment: ${fetchError.message}`);
    }

    if (!payment) {
      console.error('❌ [VOID DEBUG] Payment not found');
      throw new Error('Payment not found');
    }

    console.log('✅ [VOID DEBUG] Payment found:', {
      id: payment.id,
      amount: payment.amount,
      pdf_url: payment.pdf_url,
      booking_id: payment.booking_id
    });

    // Step 2: Delete PDF from storage if it exists
    if (payment.pdf_url) {
      try {
        console.log('🗑️ [VOID DEBUG] Deleting PDF from storage...');
        
        // Extract filename from URL
        const urlParts = payment.pdf_url.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        console.log('🗑️ [VOID DEBUG] PDF filename to delete:', filename);
        
        const { error: storageError } = await supabase.storage
          .from('receipts')
          .remove([filename]);

        if (storageError) {
          console.error('❌ [VOID DEBUG] Failed to delete PDF:', storageError);
          // Continue anyway - we still want to delete the payment record
        } else {
          console.log('✅ [VOID DEBUG] PDF deleted successfully');
        }
      } catch (pdfError) {
        console.error('❌ [VOID DEBUG] Error deleting PDF:', pdfError);
        // Continue anyway
      }
    } else {
      console.log('ℹ️ [VOID DEBUG] No PDF to delete');
    }

    // Step 3: Delete the payment record completely
    console.log('🗑️ [VOID DEBUG] Deleting payment record...');
    const { error: deleteError } = await supabase
      .from('payments')
      .delete()
      .eq('id', paymentId);

    if (deleteError) {
      console.error('❌ [VOID DEBUG] Failed to delete payment record:', deleteError);
      throw new Error(`Failed to delete payment: ${deleteError.message}`);
    }

    console.log('✅ [VOID DEBUG] Payment record deleted successfully');
    
    // Step 4: Log the void action for audit purposes
    console.log('📝 [VOID DEBUG] Payment completely voided:', {
      payment_id: paymentId,
      amount: payment.amount,
      booking_id: payment.booking_id,
      reason: reason,
      voided_at: new Date().toISOString()
    });

    console.log('🎉 [VOID DEBUG] Complete void process finished successfully');

  } catch (error) {
    console.error('❌ [VOID DEBUG] Complete void process failed:', error);
    throw error;
  }
}

async function refundPayment(id: string, amount: number, reason: string) {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'refunded',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}